import { useEffect, useState } from 'react';
import { useQueryClient } from 'react-query';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { isEmpty } from 'lodash';
import { CartFill, ChatFill, Check, SettingFill } from '@peacecover/icon';
import { Button } from '@peacecover/component';
import updateUserPreferences from '../../components/api/mutations/updateUserPreferences';
import useCommentsQuery from '../../components/api/queries/useCommentsQuery';
import usePostsQuery from '../../components/api/queries/usePostsQuery';
import { useHandleHistoryBack } from '../../utils/useHandleHistoryBack';
import ToggleFollow from '../../components/api/mutations/ToggleFollow';
import { useGetUserInfo } from '../../domains/user/ui/hooks/useGetUserInfo';
import { useGetUserProfile } from '../../domains/user/ui/hooks/useGetUserProfile';
import { useCreateDMChatRoom } from './hooks/useCreateDMChatRoom';
import { useCoverTabsToAnchor } from './hooks/useCoverTabsToAnchor';
import StickyHeader from '../components/bar/StickyHeader';
import GradientLoadingSpinner from '../components/GradientLoadingSpinner';
import InfiniteScroll from '../../components/InfiniteScroll';
import AddUserProfileV1BottomSheet from '../../components/bottomSheet/AddUserProfileV1BottomSheet';
import PostPreview from '../../components/community/PostPreview';
import PostsList from '../../components/community/PostsList';
import TabsListSelector from '../../components/community/TabsListSelector';
import SocialProfileSection from './components/SocialProfileSection';
import { MenuDropdown, MenuDropdownItem } from '../components/popup/MenuDropdown';
import EmptyCase from '../../components/errors/EmptyCase';
import type { components } from '../../types/schema';

interface TabProps {
  id: string;
  text: string;
}

type User = components['schemas']['User'];
type UserProfile = components['schemas']['UserProfile'];
type FeedProfile = components['schemas']['FeedProfile'];
type Comment = components['schemas']['Comment'];
type Post = components['schemas']['PostPreview'];

export const UserFeedProfilePage = () => {
  const queryClient = useQueryClient();
  const params = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [selectedTab, setSelectedTab] = useState<TabProps | null>(null);
  const [isShowBaseProfileBottomSheet, setIsShowBaseProfileBottomSheet] = useState(false);
  const [isCardOpen, setIsCardOpen] = useState(false);
  const [isShowSettingMenu, setIsShowSettingMenu] = useState(false);
  const {
    containerRef: tabsContainerRef,
    anchorRef: fanBuddyCardStyleAnchorRef,
    containerStyle: tabsContainerStyle,
    recomputeCoverOffset: recomputeTabsCoverOffset
  } = useCoverTabsToAnchor({ enabled: !isCardOpen });

  const userProfileId = params.user_profile_id;

  const { data: user } = useGetUserInfo();
  const { data: userProfile } = useGetUserProfile(userProfileId ?? '') as unknown as {
    data: FeedProfile;
  };
  const { handleCreateDMChatRoom } = useCreateDMChatRoom({
    userProfileId: user?.baseUserProfile?.userProfileId ?? ''
  });

  const handleHistoryBack = useHandleHistoryBack();

  const onSelectedTab = (tab: TabProps) => {
    setSelectedTab(tab);
  };

  // userProfile 로딩 완료 등으로 레이아웃이 변하면 한번 더 맞춘다
  useEffect(() => {
    if (!isEmpty(userProfile)) {
      recomputeTabsCoverOffset();
    }
  }, [userProfile, recomputeTabsCoverOffset]);

  const invalidateUserProfile = () => {
    queryClient.invalidateQueries(['userProfile']);
    queryClient.invalidateQueries(['user']);
    queryClient.invalidateQueries(['Posts']);
    queryClient.invalidateQueries(['buddyPreference']);

    window.opener?.postMessage(
      {
        type: 'invalidated-user-profile',
        params: {
          userProfileId: params.user_profile_id
        }
      },
      window.location.origin
    );
  };

  const toggleFollow = async (isFollowed: boolean) => {
    const response = await ToggleFollow({
      isFollowed: isFollowed,
      currentUserProfileId: user?.baseUserProfile?.userProfileId,
      followUserProfileId: userProfile?.userProfileId
    });
    if (response.ok) {
      invalidateUserProfile();
    }
  };

  const renderSelectedTab = () => {
    if (selectedTab?.id === 'posts') {
      return <RenderPosts user={user} userProfile={userProfile} selectedTab={selectedTab} />;
    }
    if (selectedTab?.id === 'anonymous') {
      return (
        <>
          <div className="bg-surface-lv1 text-on-disable text-center text-[13px] leading-[16px] py-[16px]">
            {t('userFeedProfilePage.anonymousOnlyToYou')}
          </div>
          <RenderPosts user={user} userProfile={userProfile} selectedTab={selectedTab} />
        </>
      );
    }
    if (selectedTab?.id === 'comments') {
      return <RenderComments user={user} userProfile={userProfile} />;
    }
    return null;
  };

  if (isEmpty(user) || isEmpty(userProfile)) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center">
        <GradientLoadingSpinner size={36} />
      </div>
    );
  }

  return (
    <div className="bg-background-light dark:bg-background-dark pt-[env(safe-area-inset-top)] pb-[env(safe-area-inset-bottom)]">
      <StickyHeader
        showBackButton={true}
        title={t('userFeedProfilePage.pageTitle')}
        onClickBackButton={handleHistoryBack}
        rightContent={
          userProfile?.isMine && (
            <div className="relative">
              <button type="button" onClick={() => setIsShowSettingMenu(true)}>
                <SettingFill size={24} className="text-text-main-lv1" />
              </button>
              {isShowSettingMenu && (
                <FeedProfileMenus
                  setIsShowBaseProfileBottomSheet={setIsShowBaseProfileBottomSheet}
                  showTradingProfile={userProfile?.showTradingProfile ?? false}
                  invalidateUserProfile={invalidateUserProfile}
                  setIsShowSettingMenu={setIsShowSettingMenu}
                />
              )}
            </div>
          )
        }
      />
      <div className="mt-[55px]">
        <SocialProfileSection
          userProfileId={userProfile.userProfileId}
          isMine={userProfile.isMine}
          followingsCount={userProfile.followingsCount ?? 0}
          followersCount={userProfile.followersCount ?? 0}
          handleClickCard={() => {
            setIsCardOpen((prev) => !prev);
          }}
          handleOpenCard={() => {
            setIsCardOpen(true);
          }}
          styleAnchorRef={fanBuddyCardStyleAnchorRef}
          onCardLayoutReady={recomputeTabsCoverOffset}
        />
        <div
          ref={tabsContainerRef}
          className="pt-[19px] relative bg-background-light dark:bg-background-dark z-[20] transition-all duration-200"
          style={{
            ...(isCardOpen ? { marginTop: 0 } : tabsContainerStyle)
          }}
        >
          <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[calc(100%-48px)] h-[19px] overflow-hidden z-0 pointer-events-none shadow-[0_-8px_12.7px_-5px_rgba(0,0,0,0.08)] dark:shadow-[0_-8px_12.7px_-5px_rgba(0,0,0,0.54)]" />
          <div className="relative z-10 flex items-center justify-between px-[24px]">
            <TabsListSelector
              onSelectedTab={(tab) => onSelectedTab(tab)}
              hiddenTabId={userProfile.isMine ? '' : 'anonymous'}
            />
            <div className="flex items-center gap-[8px]">
              {!userProfile?.isMine ? (
                userProfile.isFollowed ? (
                  <Button
                    size="medium"
                    variant="line"
                    btnColor="gray"
                    onClick={() => toggleFollow(userProfile.isFollowed)}
                    className="rounded-[12px] font-semibold text-[14px] min-w-[116px] flex items-center justify-center gap-[4px] border-separator1"
                  >
                    {userProfile.isFollowing
                      ? t('myUserProfileList.mutualFollow')
                      : t('myUserProfileList.following')}
                    <Check size={15} />
                  </Button>
                ) : (
                  <Button
                    size="medium"
                    btnColor="black"
                    onClick={() => toggleFollow(userProfile.isFollowed)}
                    className="rounded-[12px] font-semibold text-[14px] min-w-[116px] dark:text-pure-black"
                  >
                    {t('myUserProfileList.follow')}
                  </Button>
                )
              ) : null}
              {!userProfile.isMine &&
                userProfile.showTradingProfile &&
                userProfile.tradingUserProfileId && (
                  <button
                    type="button"
                    className="p-[7.5px] bg-secondary-light dark:bg-secondary-dark rounded-[12px]"
                    onClick={() =>
                      navigate(`/trading/user_profile/${userProfile.tradingUserProfileId}`)
                    }
                  >
                    <CartFill size={24} className="text-primary-light dark:text-primary-dark" />
                  </button>
                )}
            </div>
          </div>
          {renderSelectedTab()}
        </div>
        {user.baseUserProfile?.userProfileId && isShowBaseProfileBottomSheet && (
          <AddUserProfileV1BottomSheet
            title={t('myUserProfileList.changeProfile')}
            userProfile={user.baseUserProfile}
            isShowProfileBottomSheet={isShowBaseProfileBottomSheet}
            setIsShowProfileBottomSheet={setIsShowBaseProfileBottomSheet}
            category={'base'}
            isMergeProfile={false}
            onCompleted={invalidateUserProfile}
          />
        )}
      </div>
      {!userProfile.isMine && userProfile.isFollowed && userProfile.isFollowing && (
        <button
          type="button"
          className="fixed bottom-[24px] right-[24px] p-[15px] w-[60px] h-[60px] bg-primary-light dark:bg-primary-dark rounded-full flex items-center justify-center z-[100]"
          onClick={() => handleCreateDMChatRoom(userProfileId ?? '')}
        >
          <ChatFill size={30} className="text-pure-white" />
        </button>
      )}
    </div>
  );
};

export default UserFeedProfilePage;

interface FeedProfileMenusProps {
  setIsShowBaseProfileBottomSheet: (isShow: boolean) => void;
  showTradingProfile: boolean;
  invalidateUserProfile: () => void;
  setIsShowSettingMenu: (isShow: boolean) => void;
}

const FeedProfileMenus = ({
  setIsShowBaseProfileBottomSheet,
  showTradingProfile,
  invalidateUserProfile,
  setIsShowSettingMenu
}: FeedProfileMenusProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();

  const onToggleShowTradingProfile = async () => {
    const response = await updateUserPreferences({
      showTradingProfile: !showTradingProfile
    });
    if (response?.ok) {
      invalidateUserProfile();
    }
  };

  return (
    <MenuDropdown
      className="w-fit right-0 top-[27px] absolute"
      onClickOutside={() => setIsShowSettingMenu(false)}
    >
      <MenuDropdownItem
        onClick={() => setIsShowBaseProfileBottomSheet(true)}
        className="!text-left"
      >
        {t('userFeedProfilePage.menuChangeNickname')}
      </MenuDropdownItem>
      <MenuDropdownItem
        onClick={() => navigate('/fan-buddy/intro', { state: { redirectPath: location.pathname } })}
        className="!text-left"
      >
        {t('userFeedProfilePage.createProfileCard')}
      </MenuDropdownItem>
      <MenuDropdownItem onClick={() => onToggleShowTradingProfile()} className="!text-left">
        {showTradingProfile
          ? t('userFeedProfilePage.menuHiddenTradingProfile')
          : t('userFeedProfilePage.menuOpenTradingProfile')}
      </MenuDropdownItem>
    </MenuDropdown>
  );
};

interface RenderPostsProps {
  user?: User;
  userProfile: UserProfile;
  selectedTab: TabProps;
}

const RenderPosts = ({ user, userProfile, selectedTab }: RenderPostsProps) => {
  const [paginatedPosts, setPaginatedPosts] = useState<Post[]>([]);

  const { data, isFetching, fetchNextPage, hasNextPage } = usePostsQuery({
    userProfileId: userProfile?.userProfileId,
    anonymous: selectedTab?.id === 'anonymous',
    perPage: 15,
    onlyFollowers: false
  });

  const loadMore = () => {
    if (!isFetching && hasNextPage) {
      fetchNextPage();
    }
  };

  useEffect(() => {
    if (!isEmpty(data)) {
      const posts = data.pages?.flatMap((page) => page.posts.map((post: Post) => post));
      setPaginatedPosts(posts);
    }
  }, [data]);

  return (
    userProfile &&
    paginatedPosts && (
      <PostsList user={user} posts={paginatedPosts} loadMore={loadMore} hasMore={hasNextPage} />
    )
  );
};

interface RenderCommentsProps {
  user?: User;
  userProfile: UserProfile;
}

const RenderComments = ({ user, userProfile }: RenderCommentsProps) => {
  const { t } = useTranslation();
  const { data, isFetching, fetchNextPage, hasNextPage } = useCommentsQuery({
    userProfileId: userProfile?.userProfileId,
    perPage: 30
  });

  const [paginatedComments, setPaginatedComments] = useState<Comment[]>([]);

  useEffect(() => {
    if (!isEmpty(data)) {
      const posts = data.pages?.flatMap((page) => page.comments.map((comment: Comment) => comment));
      setPaginatedComments(posts);
    }
  }, [data]);

  return userProfile.userProfileId && paginatedComments.length > 0 ? (
    <InfiniteScroll
      items={paginatedComments}
      loadMore={fetchNextPage}
      isFetching={isFetching}
      hasNextPage={hasNextPage}
      // @ts-ignore
      renderItem={(item) => (
        <div key={item.postId} className="border-b-[1px] border-separator1 px-[24px] py-[16px]">
          <PostPreview user={user} post={item} />
        </div>
      )}
    />
  ) : (
    // @ts-ignore
    <EmptyCase description={t('userFeedProfilePage.emptyComment')} />
  );
};
